import type { Antiguedad, Etapa, FilaVista, SuplenteFila } from '../types/metas.types';
import { esMetaABodeguero, TEXTO_META_A_BODEGUERO } from './bodeguero';
import { esFilaCasa, esMetaACasa, TEXTO_META_A_CASA } from './casa';
import { primeraSemanaSinBono } from './catalogo';
import type { SeveridadTag } from './estado';
import { fechaCorta } from './semanas';

/**
 * Los distintivos junto al nombre de cada colaborador: lo que el supervisor
 * necesita saber de esa persona ANTES de escribirle una meta.
 *
 * La antigüedad sale de BUK (`start_date`) con la regla del spec de comisiones
 * —la calcula la API—; aquí sólo se nombra. Un vendedor en inducción o
 * garantía sin meta no puede cobrar comisión, por eso se ve a primera vista.
 */
export interface Distintivo {
  texto: string;
  severidad: SeveridadTag;
  /** Explicación larga, para el `title`. */
  detalle?: string;
}

/**
 * «semanas 4 a 12», «semana 4 en adelante»: las semanas de la etapa con la regla
 * de la semana que se ve. Las manda la API (`semanasEtapa`) y no se escriben aquí:
 * desde la S45 de 2026 ya no hay garantía (21-oct-2026, decisión del usuario), y
 * un «semanas 4 a 12» fijo seguiría prometiendo una garantía que ya no se paga.
 * `null` si la API no las manda (una anterior al cambio).
 */
export function semanasDeEtapa(a: Antiguedad | null | undefined): string | null {
  const r = a?.semanasEtapa;
  if (!r) return null;
  if (r.hasta === null) return `semana ${r.desde} en adelante`;
  if (r.hasta === r.desde) return `semana ${r.desde}`;
  return `semanas ${r.desde} a ${r.hasta}`;
}

const NOMBRE_ETAPA: Record<Etapa, string> = {
  posterior: 'Alta posterior a la semana',
  ingreso: 'Ingresó esta semana (no cuenta)',
  induccion: 'Inducción',
  garantia: 'Garantía',
  vendedor: 'Vendedor',
};

/** La antigüedad en una línea, para «Historial»: «Garantía (semanas 4 a 12) · semana 7». */
export function textoAntiguedad(a: Antiguedad | null | undefined): string {
  if (!a?.etapa) return '—';
  const semanas = semanasDeEtapa(a);
  return `${NOMBRE_ETAPA[a.etapa] ?? a.etapa}${semanas ? ` (${semanas})` : ''}${a.semana ? ` · semana ${a.semana}` : ''}`;
}

const conMayuscula = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function distintivosDe(f: FilaVista): Distintivo[] {
  // La Casa (ronda F, 23-oct-2026) no es una persona: sin antigüedad, BUK ni
  // suplente. «Casa» siempre y, con meta, «Meta a Casa» (como el bodeguero).
  if (esFilaCasa(f))
    return [
      {
        texto: 'Casa',
        severidad: 'secondary',
        detalle: 'La Casa de la tienda: la venta que se cobra con la cuenta «CASA…» del ERP. Su meta cuenta para la meta de la tienda; nadie la cobra, ni el gerente.',
      },
      ...(esMetaACasa(f)
        ? [
            {
              texto: 'Meta a Casa',
              severidad: 'warning' as SeveridadTag,
              detalle: `${TEXTO_META_A_CASA}. Es opcional y no impide enviar: el regional lo ve al autorizar. No se copia a la semana siguiente.`,
            },
          ]
        : []),
    ];
  const out: Distintivo[] = [];
  const a = f.antiguedad;
  const semanas = semanasDeEtapa(a);
  switch (a?.etapa) {
    case 'induccion':
      out.push({
        texto: `Nuevo · sem ${a.semana}`,
        severidad: 'info',
        detalle: semanas ? `${conMayuscula(semanas)} desde su ingreso (inducción).` : 'En su inducción.',
      });
      break;
    case 'garantia':
      out.push({
        texto: `Garantía · sem ${a.semana}`,
        severidad: 'info',
        detalle: semanas ? `${conMayuscula(semanas)} desde su ingreso (garantía).` : 'En su periodo de garantía.',
      });
      break;
    case 'ingreso':
      out.push({
        texto: 'Ingresó esta semana',
        severidad: 'info',
        detalle: 'Entró de jueves a domingo: esta semana no cuenta y su semana 1 es la siguiente.',
      });
      break;
    case 'posterior':
      out.push({
        texto: 'Alta posterior',
        severidad: 'warning',
        detalle: 'Su fecha de alta en BUK es posterior a esta semana: no debería llevar meta.',
      });
      break;
    default:
      break;
  }
  if (f.estadoBuk && f.estadoBuk.trim().toLowerCase() !== 'activo') {
    out.push({ texto: 'Inactivo en BUK', severidad: 'danger', detalle: `Estado en BUK: ${f.estadoBuk}.` });
  }
  if (f.puestoFuente === 'proscai') {
    out.push({
      texto: 'Puesto del ERP',
      severidad: 'secondary',
      detalle: 'BUK no trae su puesto; el que se ve es el del grupo de usuario en Proscai.',
    });
  }
  if (f.enPlantilla === false) {
    out.push({
      texto: 'Ya no está en la plantilla',
      severidad: 'warning',
      detalle: 'Está capturado pero ya no sale en la plantilla de BUK de esta tienda. ¿Es baja o cambió de tienda?',
    });
  }
  if (f.otraTienda) {
    out.push({
      texto: `Meta en ${f.otraTienda}`,
      severidad: 'warning',
      detalle: 'Una persona lleva una sola meta por semana. Si no trabaja aquí, marca «Tienda errónea».',
    });
  }
  if (f.origen === 'vendio_aqui') {
    out.push({
      texto: 'Vendió aquí',
      severidad: 'secondary',
      detalle: 'No es de la plantilla de la tienda, pero tiene venta en ella.',
    });
  }
  // 23-oct-2026: se ve en la fila (tabla, tarjeta del teléfono y lectura del regional)
  // en cuanto se escribe; el aviso completo va al final de la tienda.
  if (esMetaABodeguero(f)) {
    out.push({
      texto: 'Meta a bodeguero',
      severidad: 'warning',
      detalle: `${TEXTO_META_A_BODEGUERO}. Es opcional y no impide enviar: el regional lo ve al autorizar. No se copia a la semana siguiente.`,
    });
  }
  const suplente = distintivoSuplente(f.suplente);
  if (suplente) out.push(suplente);
  return out;
}

/**
 * El programa de suplente de la persona (fase 3): propuesto, vigente o
 * certificado. «Vigente» lo dice la API para la semana de la vista
 * (`metas_suplentes_vigentes`); aquí no se recalcula.
 */
export function distintivoSuplente(s: SuplenteFila | null | undefined): Distintivo | null {
  if (!s) return null;
  const desde = fechaCorta(s.inicioDesarrollo);
  if (s.estado === 'propuesta')
    return {
      texto: 'Suplente propuesto',
      severidad: 'warning',
      detalle: `En desarrollo desde el ${desde}: espera la autorización del regional y, mientras, no cobra el bono.`,
    };
  if (s.vigenteEstaSemana)
    return {
      texto: 'Suplente vigente',
      severidad: 'success',
      detalle: s.certificadoEn
        ? `En desarrollo desde el ${desde}; se certificó el ${fechaCorta(s.certificadoEn)} y deja de cobrar el bono desde la semana del ${fechaCorta(primeraSemanaSinBono(s.certificadoEn))}.`
        : `En desarrollo desde el ${desde}: cobra el bono de suplente esta semana, hasta certificarse o cumplir 3 meses.`,
    };
  if (s.certificadoEn)
    return {
      texto: 'Suplente certificado',
      severidad: 'secondary',
      detalle: `Se certificó el ${fechaCorta(s.certificadoEn)}: esta semana ya no cobra el bono de suplente.`,
    };
  return {
    texto: 'Suplente sin bono esta semana',
    severidad: 'secondary',
    detalle: `Autorizado desde el ${desde}, pero esta semana no cobra: empieza después o ya pasaron los 3 meses.`,
  };
}
